// Running a replay: starting it, advancing it frame by frame, pausing at chapters, jumping between them.

import { loadDetail, type Detail, type ReplayChapter, type ReplayDef } from "./data";
import { buildWarp, chapterAt, chapterHold, progressAt, replaySeconds, timeAt } from "./replay";
import { useApp, type ReplayRun } from "./store";

const LEAD_IN = 1.2; // seconds before a replay starts moving: the camera arrives first
const MAX_AUTO = 8;

/** Without curated chapters, a replay pauses at the war's best-known battles (those in the most Wikipedia
 * languages), at most eight, spread over the war. */
export function autoChapters(d: Detail, t0: number, t1: number): ReplayChapter[] {
  const want = Math.min(MAX_AUTO, Math.round(Math.log2(1 + d.events.length) * 1.4));
  const gap = (t1 - t0) * 0.05;
  const picked: Detail["events"] = [];
  for (const e of [...d.events].sort((a, b) => b.s - a.s)) {
    if (picked.length >= want) break;
    if (e.x == null || e.y == null || !e.date || e.s < 3 || e.t0 < t0 || e.t0 >= t1) continue;
    if (picked.some((p) => Math.abs(p.t0 - e.t0) < gap)) continue;
    picked.push(e);
  }
  return picked.sort((a, b) => a.t0 - b.t0).map((e) => ({
    t: Math.min(e.t0 + 0.5 / 365, t1 - 1e-6), d: e.date!, x: e.name, v: [e.x!, e.y!, 5.5], r: e.id,
    u: e.u ?? (/^Q\d+$/.test(e.id) ? `https://www.wikidata.org/wiki/${e.id}` : ""),
  }));
}

/** Start a replay: a curated one, or any war by its index (paced by its battles, pausing at the best known). */
export async function startReplay(target: { def?: ReplayDef; i?: number }, opts: { leadIn?: number } = {}): Promise<void> {
  const st = useApp.getState();
  const c = st.conflicts;
  if (!c) return;
  const def = target.def ?? (target.i != null ? st.replays?.find((r) => r.i === target.i) : undefined) ?? null;
  const i = def ? def.i : target.i ?? -1;
  if (i < 0 || i >= c.n) return;
  const t0 = def ? def.t0 : c.t0[i];
  const t1 = Math.min(def ? def.t1 : c.og[i] ? st.now : c.t1[i], st.now);
  if (!(t1 > t0)) return;
  let pace = def?.pace ?? null, bb = c.bb[i], chapters = def?.ch ?? [];
  if (!def || !def.v) {
    const d = await loadDetail(c.id[i]).catch(() => null);
    if (d?.replay?.bb) bb = d.replay.bb;
    if (!def && d) {
      pace = d.replay?.pace ?? null;
      chapters = autoChapters(d, t0, t1);
    }
  }
  const run: ReplayRun = {
    key: def?.id ?? c.id[i], def, i, title: def?.title ?? c.name[i], t0, t1, seconds: def?.s ?? replaySeconds(t0, t1),
    warp: buildWarp(t0, t1, pace), chapters, hold: opts.leadIn ?? LEAD_IN, tSet: t0, done: false,
  };
  st.beginReplay(run);
  st.patchReplay({ tSet: useApp.getState().t });
  if (window.matchMedia("(max-width: 900px)").matches) st.setSheet("collapsed");
  if (def?.v) st.fly({ lon: def.v[0], lat: def.v[1], zoom: def.v[2], exact: true, duration: 1600 });
  else if (bb) st.fly({ bbox: bb });
  else if (c.hasPoint[i]) st.fly({ lon: c.lon[i], lat: c.lat[i], zoom: 4, exact: true });
}

function flyTo(ch: ReplayChapter | undefined) {
  if (ch?.v) useApp.getState().fly({ lon: ch.v[0], lat: ch.v[1], zoom: ch.v[2], exact: true, duration: 1800 });
}

/** Advance the replay by dt seconds of wall time (called every animation frame while playing). */
export function stepReplay(dt: number): void {
  const st = useApp.getState();
  const r = st.replay;
  if (!r) return;
  const t = st.t;
  let hold = Math.abs(t - r.tSet) > 1e-9 ? 0 : r.hold; // the user moved the playhead: carry on from there
  if (hold > 0) {
    st.patchReplay({ hold: Math.max(0, hold - dt * Math.max(1, st.speed)), tSet: t });
    return;
  }
  const end = Math.min(r.t1, st.now);
  let next = timeAt(r.warp, Math.min(1, progressAt(r.warp, t) + (dt * st.speed) / r.seconds));
  const k = chapterAt(r.chapters, t) + 1;
  if (k < r.chapters.length && next >= r.chapters[k].t) {
    next = r.chapters[k].t;
    hold = chapterHold(r.chapters[k].x);
    flyTo(r.chapters[k]);
  }
  const done = next >= end - 1e-9;
  st.setT(done ? end : next);
  st.patchReplay({ hold, tSet: useApp.getState().t, done });
  if (done) st.setPlaying(false);
}

/** Show chapter k: the playhead at its date, the camera on its place (paused there while playing). */
export function jumpToChapter(k: number): void {
  const st = useApp.getState();
  const r = st.replay;
  if (!r) return;
  if (k < 0) {
    st.setT(r.t0);
    st.patchReplay({ hold: 0, tSet: useApp.getState().t, done: false });
    return;
  }
  const ch = r.chapters[Math.min(k, r.chapters.length - 1)];
  if (!ch) return;
  st.setT(ch.t);
  st.patchReplay({ hold: st.playing ? chapterHold(ch.x) : 0, tSet: useApp.getState().t, done: false });
  flyTo(ch);
}

/** Next (+1) or previous (-1) chapter; "previous" first returns to the start of the current chapter. */
export function stepChapter(dir: 1 | -1): void {
  const r = useApp.getState().replay;
  if (!r) return;
  const t = useApp.getState().t;
  const k = chapterAt(r.chapters, t);
  if (dir > 0) {
    if (k + 1 < r.chapters.length) jumpToChapter(k + 1);
    return;
  }
  const back = k >= 0 && t - r.chapters[k].t > (r.t1 - r.t0) * 0.004 ? k : k - 1;
  jumpToChapter(back);
}

/** Jump to a point of the replay by its progress (0–1), as on the progress bar. */
export function seekReplay(u: number): void {
  const st = useApp.getState();
  const r = st.replay;
  if (!r) return;
  st.setT(timeAt(r.warp, Math.min(1, Math.max(0, u))));
  st.patchReplay({ hold: 0, tSet: useApp.getState().t, done: false });
}

export function restartReplay(): void {
  const st = useApp.getState();
  const r = st.replay;
  if (!r) return;
  st.setT(r.t0);
  st.patchReplay({ hold: LEAD_IN, tSet: useApp.getState().t, done: false });
  if (r.def?.v) st.fly({ lon: r.def.v[0], lat: r.def.v[1], zoom: r.def.v[2], exact: true, duration: 1600 });
  else if (r.chapters[0]) flyTo(r.chapters[0]);
  st.setPlaying(true);
}

/** Play/pause: a finished replay starts again; plain playback at today rewinds a little first. */
export function togglePlay(): void {
  const st = useApp.getState();
  if (st.replay) {
    if (st.replay.done) restartReplay();
    else st.setPlaying(!st.playing);
    return;
  }
  if (!st.playing && st.t >= st.now - 1e-3) st.setT(st.now - st.span * 0.45);
  st.setPlaying(!st.playing);
}
